import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@cargomatic/ui';
import AddButton from 'components/v2/buttons/AddButton';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import CargomaticInput from 'components/v2/input';
import Pagination from 'components/v2/PaginationV2';
import CargomaticSelect from 'components/v2/select';
import ToolTip from 'components/v2/Tooltip';
import { LoaderCircle } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import { showToast } from 'stores/ToastStore';
import { unescapeHTML } from 'underscore.string';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { createException, useGetBridgeReasonCodes, useGetExceptions, } from 'utils/BridgeCodeAPIUtils';
const DOCUMENTS_PER_PAGE = 5;
const isApproveDisabled = (exception, reasonCodes) => {
    const hasReasonCode = !(reasonCodes === null || reasonCodes === void 0 ? void 0 : reasonCodes.length) || exception.reason_code;
    const hasBridgeCode = !!(exception === null || exception === void 0 ? void 0 : exception.bridge_code);
    const hasShipmentLeg = !!(exception === null || exception === void 0 ? void 0 : exception.shipment_leg);
    return !(hasReasonCode && hasBridgeCode && hasShipmentLeg);
};
const EditDropdown = ({ exception, disabled = false, onSelect }) => {
    const { t } = useTranslation();
    const exception_id = exception._id;
    return (React.createElement(Dropdown, { useIcon: true, className: "", testDataId: `${exception_id}-Dropdown-d41900dd`, disabled: disabled },
        React.createElement(Dropdown.Button, { dataTestId: `${exception_id}-Dropdown-button-d41909dd`, onClick: () => onSelect(exception, 'edit') }, t('EDIT')),
        React.createElement(Dropdown.Button, { dataTestId: `${exception_id}-Dropdown-button-d46900dd`, onClick: () => onSelect(exception, 'delete') }, t('DELETE'))));
};
const ExceptionActionConfirmation = ({ exception, action, disableApprove, handleApprove, handleCancel, }) => {
    const { t } = useTranslation();
    const exception_id = exception._id;
    const isEdit = action === 'edit';
    const approveTooltipLabel = isEdit
        ? t('SAVE_EXCEPTION')
        : t('DELETE_EXCEPTION');
    return (React.createElement("div", { className: "flex gap-[5px]" },
        React.createElement(ToolTip, { dataTestId: `${exception_id}-CargomaticApproveTooltip-74bc9003`, id: `${exception_id}_approve_button`, position: "left", bodyParams: {
                dataTestId: `${exception_id}-ApproveButton-c6f695g8`,
                onClick: () => {
                    handleApprove(exception_id);
                },
                disabled: disableApprove,
            }, body: ({ dataTestId, onClick, disabled }) => isEdit ? (React.createElement(ApproveButton, { dataTestId: dataTestId, onClick: onClick, disabled: disabled })) : (React.createElement(DeleteButton, { dataTestId: dataTestId, onClick: onClick, disabled: disabled })) }, approveTooltipLabel),
        React.createElement(ToolTip, { dataTestId: `${exception_id}-CargomaticDeclineTooltip-09f91576`, id: `${exception_id}_decline_button`, position: "left", bodyParams: {
                dataTestId: `${exception_id}-DeclineButton-09f91576`,
                onClick: () => {
                    handleCancel(exception_id);
                },
            }, body: ({ dataTestId, onClick }) => (React.createElement(DeclineButton, { onClick: onClick, dataTestId: dataTestId })) }, t('CANCEL'))));
};
const ExceptionActionButton = ({ exception, hasEditAccess, onDropdownSelect, }) => {
    const { t } = useTranslation();
    const exception_id = exception._id;
    if (!featureEnabled(CONFIGURED_FEATURES.BULK_MARKET_EXCEPTIONS))
        return React.createElement(React.Fragment, null);
    if (!hasEditAccess) {
        return (React.createElement(ToolTip, { dataTestId: `${exception_id}CargomaticTooltip-74bc9003`, id: `${exception_id}document_list`, position: "left", bodyParams: {
                exception_id,
                disabled: !hasEditAccess,
                onSelect: onDropdownSelect,
                exception,
            }, body: EditDropdown }, t('BRIDGE_ROLE_INFO_TOOLTIP')));
    }
    return (React.createElement(EditDropdown, { exception_id: exception_id, exception: exception, onSelect: onDropdownSelect }));
};
const CreateRow = ({ legsItemList, legsToBridgesMap, bridgesToReasonCodesMap, handleAddException, }) => {
    const { t } = useTranslation();
    const [shipmentLeg, setShipmentLeg] = useState({});
    const [bridgeCode, setBridgeCode] = useState({});
    const [reasonCode, setReasonCode] = useState({});
    const [note, setNote] = useState('');
    const bridgeItemList = useMemo(() => {
        return (legsToBridgesMap === null || legsToBridgesMap === void 0 ? void 0 : legsToBridgesMap[shipmentLeg === null || shipmentLeg === void 0 ? void 0 : shipmentLeg._id]) || [];
    }, [shipmentLeg]);
    const reasonCodesItemList = useMemo(() => {
        return (bridgesToReasonCodesMap === null || bridgesToReasonCodesMap === void 0 ? void 0 : bridgesToReasonCodesMap[bridgeCode === null || bridgeCode === void 0 ? void 0 : bridgeCode._id]) || [];
    }, [bridgeCode]);
    const isAddDisabled = useMemo(() => {
        const hasReasonCode = !(reasonCodesItemList === null || reasonCodesItemList === void 0 ? void 0 : reasonCodesItemList.length) || (reasonCode === null || reasonCode === void 0 ? void 0 : reasonCode._id);
        const hasBridgeCode = !!(bridgeCode === null || bridgeCode === void 0 ? void 0 : bridgeCode._id);
        const hasShipmentLeg = !!(shipmentLeg === null || shipmentLeg === void 0 ? void 0 : shipmentLeg._id);
        return !(hasReasonCode && hasBridgeCode && hasShipmentLeg);
    }, [reasonCode, bridgeCode, shipmentLeg]);
    const handleLegSelect = (item) => {
        setShipmentLeg(item);
        setBridgeCode({});
        setReasonCode({});
    };
    const handleBridgeSelect = (item) => {
        setBridgeCode(item);
        setReasonCode({});
    };
    const handleReasonSelect = (item) => {
        setReasonCode(item);
    };
    return (React.createElement(TableRow, null,
        React.createElement(TableCell, null,
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-92dc5d28", showCaption: true, hideLabel: true, items: legsItemList, selected: (shipmentLeg === null || shipmentLeg === void 0 ? void 0 : shipmentLeg.value) || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: handleLegSelect, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_LEG') })),
        React.createElement(TableCell, null,
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-6c1cb9a8", showCaption: true, hideLabel: true, items: bridgeItemList, selected: (bridgeCode === null || bridgeCode === void 0 ? void 0 : bridgeCode.value) || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: handleBridgeSelect, applyDefaultMargins: false, valueKey: "value", textKey: "value", caption: t('SELECT_BRIDGE') })),
        React.createElement(TableCell, null,
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-1d50d4d0", showCaption: true, hideLabel: true, items: reasonCodesItemList, selected: (reasonCode === null || reasonCode === void 0 ? void 0 : reasonCode.value) || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: handleReasonSelect, applyDefaultMargins: false, valueKey: "value", textKey: "value", caption: t('SELECT_REASON_CODE') })),
        React.createElement(TableCell, null,
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-62f5540f", hideLabel: true, type: "text", name: "template-name", className: "w-full", placeholder: t('NOTES'), value: note, onChange: (value) => {
                    setNote(value);
                }, disabled: isAddDisabled })),
        React.createElement(TableCell, null,
            React.createElement(AddButton, { dataTestId: "AddButton-0ed24a4d", onClick: () => {
                    handleAddException({ shipmentLeg, bridgeCode, reasonCode, note });
                    setShipmentLeg();
                    setBridgeCode({});
                    setReasonCode({});
                    setNote('');
                }, disabled: isAddDisabled }))));
};
const EditRow = ({ exception, legsItemList, legsToBridgesMap, bridgesToReasonCodesMap, handleApproveEdit, handleCancelEdit, }) => {
    var _a, _b;
    const { t } = useTranslation();
    const [exceptionData, setExceptionData] = useState(exception);
    const exception_id = exceptionData._id;
    const leg = (exceptionData === null || exceptionData === void 0 ? void 0 : exceptionData.shipment_leg) || '';
    const legId = (exceptionData === null || exceptionData === void 0 ? void 0 : exceptionData.shipment_leg_id) ||
        ((_a = legsItemList === null || legsItemList === void 0 ? void 0 : legsItemList.find((item) => item.value == leg)) === null || _a === void 0 ? void 0 : _a._id) ||
        '';
    const bridgeCodes = (legsToBridgesMap === null || legsToBridgesMap === void 0 ? void 0 : legsToBridgesMap[legId]) || [];
    const bridgeCode = (exceptionData === null || exceptionData === void 0 ? void 0 : exceptionData.bridge_code) || '';
    const bridgeCodeId = exceptionData.bridge_code_id ||
        ((_b = bridgeCodes.find((item) => item.value == bridgeCode)) === null || _b === void 0 ? void 0 : _b._id) ||
        '';
    const reasonCodes = (bridgesToReasonCodesMap === null || bridgesToReasonCodesMap === void 0 ? void 0 : bridgesToReasonCodesMap[bridgeCodeId]) || [];
    const disableApprove = isApproveDisabled(exceptionData, reasonCodes);
    const handleEditException = (field, value, value_id) => {
        let newException = {
            [field]: value,
            [`${field}_id`]: value_id,
        };
        if (field === 'shipment_leg') {
            Object.assign(newException, {
                bridge_code: '',
                bridge_code_id: '',
                reason_code: '',
                reason_code_id: '',
            });
        }
        else if (field === 'bridge_code') {
            Object.assign(newException, {
                reason_code: '',
                reason_code_id: '',
            });
        }
        setExceptionData((prev) => {
            return Object.assign(Object.assign({}, prev), newException);
        });
    };
    return (React.createElement(TableRow, { key: exception._id },
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticSelect, { dataTestId: `CargomaticSelect-92dc5d28-${exception_id}`, showCaption: true, hideLabel: true, items: legsItemList, selected: exceptionData.shipment_leg || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: (selectedItem) => {
                    handleEditException('shipment_leg', selectedItem.value, selectedItem._id);
                }, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_LEG') })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-6c1cb9a8", showCaption: true, hideLabel: true, items: bridgeCodes, selected: exceptionData.bridge_code || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: (selectedItem) => {
                    handleEditException('bridge_code', selectedItem.value, selectedItem._id);
                }, applyDefaultMargins: false, valueKey: "value", textKey: "value", caption: t('SELECT_BRIDGE') })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-1d50d4d0", showCaption: true, hideLabel: true, items: reasonCodes, selected: exceptionData.reason_code || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: (selectedItem) => {
                    handleEditException('reason_code', selectedItem.value, selectedItem._id);
                }, applyDefaultMargins: false, valueKey: "value", textKey: "value", caption: t('SELECT_REASON_CODE') })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-62f5540f", hideLabel: true, type: "text", name: "template-name", className: "w-full", placeholder: t('NOTES'), value: exceptionData.reason_description, onChange: (value) => {
                    handleEditException('reason_description', value);
                }, disabled: disableApprove })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(ExceptionActionConfirmation, { exception: exception, action: "edit", handleCancel: handleCancelEdit, handleApprove: () => handleApproveEdit(exceptionData), disableApprove: disableApprove, key: `${exception._id}-exception-action` }))));
};
const ReadOnlyRow = ({ action, exception, hasEditAccess, handleApproveDelete, handleCancelDelete, onDropdownSelect, }) => {
    var _a;
    const { t } = useTranslation();
    const LEG_UPPERCASE = ((_a = exception.shipment_leg) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || '';
    return (React.createElement(TableRow, { key: exception._id },
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(t(LEG_UPPERCASE))),
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(exception.bridge_code)),
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(exception.reason_code)),
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(exception.reason_description)),
        React.createElement(TableCell, { className: "break-words" }, action === 'delete' ? (React.createElement(ExceptionActionConfirmation, { exception: exception, action: "delete", key: `${exception._id}-exception-action`, handleApprove: handleApproveDelete, handleCancel: handleCancelDelete })) : (React.createElement(ExceptionActionButton, { exception: exception, hasEditAccess: hasEditAccess, onDropdownSelect: onDropdownSelect })))));
};
const ExceptionRow = ({ legsToBridgesMap, legsItemList, bridgesToReasonCodesMap, handleApproveDelete, handleUpdateException, handleCancelEdit, handleCancelDelete, onDropdownSelect, exception, hasEditAccess, editingIds, }) => {
    const exception_id = exception._id;
    const action = editingIds[exception_id];
    switch (action) {
        case 'edit':
            return (React.createElement(EditRow, { legsToBridgesMap: legsToBridgesMap, legsItemList: legsItemList, bridgesToReasonCodesMap: bridgesToReasonCodesMap, handleApproveEdit: handleUpdateException, handleCancelEdit: handleCancelEdit, exception: exception, key: `edit_row_${exception_id}` }));
        case 'delete':
            return (React.createElement(ReadOnlyRow, { handleApproveDelete: handleApproveDelete, handleCancelDelete: handleCancelDelete, action: action, hasEditAccess: hasEditAccess, exception: exception, onDropdownSelect: onDropdownSelect, key: `readonly_row_delete` }));
        default:
            return (React.createElement(ReadOnlyRow, { action: action, hasEditAccess: hasEditAccess, onDropdownSelect: onDropdownSelect, exception: exception, key: `readonly_row_read` }));
    }
};
function ShipmentBridges({ shipmentId, isPaginated = true, showHeader = true, }) {
    const { t } = useTranslation();
    const { data: defaultBridgeCodes, isLoading: defaultBridgeCodesLoading } = useGetBridgeReasonCodes({
        onError: () => {
            showToast(t('SHIPPER_BRIDGE_REASON_CODES_FETCH_FAILED'), '', 'error');
        },
    });
    const exceptionParams = useMemo(() => ({ shipment_id: shipmentId, is_active: true }), [shipmentId]);
    const { data: exceptionsData, isLoading: exceptionsLoading, refetch: refetchExceptions, } = useGetExceptions(exceptionParams, {
        onError: () => {
            showToast(t('SHIPPER_EXCEPTION_FETCH_FAILED'), '', 'error');
        },
    });
    const [page, setPage] = useState(1);
    // Map of exception_id : 'action'
    const [editingIds, setEditingIds] = useState({});
    const totalExceptions = useMemo(() => (exceptionsData === null || exceptionsData === void 0 ? void 0 : exceptionsData.length) || 0, [exceptionsData]);
    const pageCount = useMemo(() => Math.ceil(totalExceptions / DOCUMENTS_PER_PAGE), [totalExceptions]);
    const paginatedExceptions = useMemo(() => {
        if (!isPaginated) {
            return exceptionsData;
        }
        const startIndex = (page - 1) * DOCUMENTS_PER_PAGE;
        const endIndex = startIndex + DOCUMENTS_PER_PAGE;
        return (exceptionsData === null || exceptionsData === void 0 ? void 0 : exceptionsData.slice(startIndex, endIndex)) || [];
    }, [exceptionsData, page]);
    useEffect(() => {
        refreshPagination();
    }, [totalExceptions]);
    const refreshPagination = () => {
        if (page > 1 && pageCount < page) {
            setPage(page - 1);
        }
    };
    const legs = useMemo(() => {
        var _a;
        const drayageCategory = (defaultBridgeCodes === null || defaultBridgeCodes === void 0 ? void 0 : defaultBridgeCodes.find((item) => item.lineOfBusiness === 'drayage')) ||
            [];
        const shipmentLegs = ((_a = drayageCategory === null || drayageCategory === void 0 ? void 0 : drayageCategory.categories) === null || _a === void 0 ? void 0 : _a.filter((item) => item.categoryType === 'market_legs')) || [];
        return shipmentLegs;
    }, [defaultBridgeCodes]);
    const legsToBridgesMap = useMemo(() => {
        const map = {};
        legs === null || legs === void 0 ? void 0 : legs.forEach((leg) => {
            var _a;
            const bridges = ((_a = leg === null || leg === void 0 ? void 0 : leg.subCategories) === null || _a === void 0 ? void 0 : _a.map((bridge) => ({
                _id: bridge._id,
                value: bridge.reasonType,
            }))) || [];
            map[leg._id] = bridges;
        });
        return map;
    }, [legs]);
    const bridgesToReasonCodesMap = useMemo(() => {
        const map = {};
        legs.forEach((leg) => {
            var _a;
            (_a = leg === null || leg === void 0 ? void 0 : leg.subCategories) === null || _a === void 0 ? void 0 : _a.forEach((bridge) => {
                var _a;
                const bridgeId = bridge === null || bridge === void 0 ? void 0 : bridge._id;
                map[bridgeId] = (_a = bridge === null || bridge === void 0 ? void 0 : bridge.reasonCodes) === null || _a === void 0 ? void 0 : _a.map((reasonCode) => ({
                    _id: reasonCode === null || reasonCode === void 0 ? void 0 : reasonCode._id,
                    value: reasonCode.description,
                }));
            });
        });
        return map;
    }, [legs]);
    const legsItemList = useMemo(() => {
        return ((legs === null || legs === void 0 ? void 0 : legs.map((category) => {
            var _a;
            const CATEGORY_UPPERCASE = ((_a = category === null || category === void 0 ? void 0 : category.name) === null || _a === void 0 ? void 0 : _a.toUpperCase()) || '';
            return {
                _id: category._id,
                value: category.name,
                localizationSlug: CATEGORY_UPPERCASE,
            };
        })) || []);
    }, [legs]);
    const hasEditAccess = useMemo(() => SessionStore.isBridgeCodeManager());
    const handleClearForm = (exceptionId) => {
        if (exceptionId) {
            return setEditingIds((prev) => {
                const updated = Object.assign({}, prev);
                delete updated[exceptionId];
                return updated;
            });
        }
        return setEditingIds({});
    };
    const handleAddException = async ({ shipmentLeg, bridgeCode, reasonCode, note = '', }) => {
        const shipment_leg = shipmentLeg.value || '';
        const bridge_code = bridgeCode.value || '';
        const reason_code = reasonCode.value || '';
        const trimmedNote = (note === null || note === void 0 ? void 0 : note.trim()) || '';
        const createBody = {
            shipment_id: shipmentId,
            shipment_leg,
            bridge_code,
            reason_code,
            reason_description: trimmedNote,
        };
        const body = {
            create: [createBody],
        };
        try {
            const response = await createException(body);
            if (response.executedOperations.length === 1) {
                showToast(t('MARKET_EXCEPTION_CREATION_SUCCESS'));
            }
            else {
                showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
            }
            refetchExceptions();
            // handleClearForm();
        }
        catch (_error) {
            showToast(t('MARKET_EXCEPTION_CREATION_FAIL'), '', 'error');
        }
    };
    const handleUpdateException = async (exceptionData) => {
        const { shipment_leg, bridge_code, reason_code, reason_description } = exceptionData;
        const updateBody = {
            shipment_leg,
            bridge_code,
            reason_code,
            reason_description,
            exception_id: exceptionData._id,
            shipment_id: shipmentId,
        };
        const body = {
            update: [updateBody],
        };
        try {
            const response = await createException(body);
            if (response.executedOperations.length >= 1) {
                showToast(t('MARKET_EXCEPTION_UPDATION_SUCCESS'));
            }
            else {
                showToast(t('MARKET_EXCEPTION_UPDATION_FAIL'), '', 'error');
            }
            refetchExceptions();
            handleClearForm(exceptionData._id);
        }
        catch (_error) {
            showToast(t('MARKET_EXCEPTION_UPDATION_FAIL'), '', 'error');
        }
    };
    const handleDeleteException = async (exception_id) => {
        const deleteBody = {
            exception_id,
            shipment_id: shipmentId,
        };
        const body = {
            delete: [deleteBody],
        };
        try {
            const response = await createException(body);
            if (response.executedOperations.length === 1) {
                showToast(t('MARKET_EXCEPTION_DELETION_SUCCESS'));
            }
            else {
                showToast(t('MARKET_EXCEPTION_DELETION_FAIL'), '', 'error');
            }
            refetchExceptions();
            handleClearForm(exception_id);
        }
        catch (_error) {
            showToast(t('MARKET_EXCEPTION_DELETION_FAIL'), '', 'error');
        }
    };
    const handleCancelEdit = (exception_id) => {
        setEditingIds((prev) => {
            const updated = Object.assign({}, prev);
            delete updated[exception_id];
            return updated;
        });
    };
    const handleCancelDelete = (exception_id) => {
        setEditingIds((prev) => {
            const updated = Object.assign({}, prev);
            delete updated[exception_id];
            return updated;
        });
    };
    const handleApproveDelete = (exception_id) => {
        handleDeleteException(exception_id);
    };
    const onDropdownSelect = (exception, action) => {
        setEditingIds((prev) => (Object.assign(Object.assign({}, prev), { [exception._id]: action })));
    };
    if (defaultBridgeCodesLoading || exceptionsLoading) {
        return (React.createElement(LoaderCircle, { className: "w-4 h-4 animate-spin ml-2", "data-testid": "LoaderCircle-f4ff494b" }));
    }
    return (React.createElement(CargomaticCollapsibleCard, { id: "shipment-bridges", dataTestId: "CargomaticCard-f4ff494b", header: () => React.createElement("span", null, t('SHIPMENT_BRIDGE_AND_EVENT_CODES')) },
        React.createElement("div", { className: "overflow-x-auto" },
            React.createElement(Table, { className: "min-w-[700px]" },
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, { className: "w-[22.5%]", "data-testid": "TableHead-f4ff494b" }, t('SHIPMENT_LEG')),
                        React.createElement(TableHead, { className: "w-[22.5%]", "data-testid": "TableHead-f4ff494t" }, t('BRIDGE')),
                        React.createElement(TableHead, { className: "w-[22.5%]", "data-testid": "TableHead-f4ff494u" }, t('BRIDGE_REASON_CODE')),
                        React.createElement(TableHead, { className: "min-w-[22.5%]", "data-testid": "TableHead-f4ff494r" }, t('NOTES')),
                        React.createElement(TableHead, { className: "w-[10%]", "data-testid": "TableHead-f4ff494i" }, t('ACTION')))),
                React.createElement(TableBody, null,
                    React.createElement(CreateRow, { legsToBridgesMap: legsToBridgesMap, legsItemList: legsItemList, bridgesToReasonCodesMap: bridgesToReasonCodesMap, handleAddException: handleAddException, key: "create-row" }), paginatedExceptions === null || paginatedExceptions === void 0 ? void 0 :
                    paginatedExceptions.map((exception, index) => (React.createElement(ExceptionRow, { legsToBridgesMap: legsToBridgesMap, legsItemList: legsItemList, bridgesToReasonCodesMap: bridgesToReasonCodesMap, handleApproveDelete: handleApproveDelete, handleUpdateException: handleUpdateException, handleCancelEdit: handleCancelEdit, handleCancelDelete: handleCancelDelete, onDropdownSelect: onDropdownSelect, exception: exception, hasEditAccess: hasEditAccess, editingIds: editingIds, key: `exception_row_${index}` }))))),
            isPaginated && (React.createElement(Pagination, { className: "border-t-0", perPage: DOCUMENTS_PER_PAGE, page: page, pages: pageCount, total: totalExceptions, showCount: true, onPageClick: (page) => {
                    setPage(page);
                    handleClearForm();
                } })))));
}
ShipmentBridges.propTypes = {
    shipmentId: PropTypes.string.isRequired,
};
ShipmentBridges.defaultProps = { shipmentId: '' };
export default ShipmentBridges;
